import { motion } from "framer-motion";
import { ShieldCheck, Certificate, Star, Trophy } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";

const icons = [Star, Trophy, Certificate, ShieldCheck];

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.85, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Partners = () => {
  const { t } = useLang();
  const p = t.partners;
  return (
    <section
      id="partners"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-36 border-y border-[#232326]/8"
      data-testid="partners-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#13535A]" />
              <span>{p.overline}</span>
            </div>
          </motion.div>
          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.6rem)] whitespace-pre-line mb-6"
            >
              {p.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="text-[#232326]/70 max-w-2xl leading-relaxed text-[1rem]"
            >
              {p.lead}
            </motion.p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {p.items.map((it, i) => {
            const Icon = icons[i] || Certificate;
            return (
              <motion.div
                key={it.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={revealUp}
                custom={i}
                className="relative bg-[#E6E3DC] p-8 md:p-10 clip-card group transition-colors duration-500 hover:bg-[#13535A] hover:text-[#F1EEE8]"
                data-testid={`partner-${i}`}
              >
                <div className="flex items-start justify-between gap-4 mb-8">
                  <span className="cert-tag group-hover:border-[#F1EEE8]/40 group-hover:text-[#F1EEE8] flex-1">
                    {it.label}
                  </span>
                  <Icon size={20} weight="light" className="text-[#13535A] group-hover:text-[#B08F5C] transition-colors shrink-0 mt-1" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight mb-4">
                  {it.name}
                  <span className="text-[#13535A] group-hover:text-[#B08F5C]">.</span>
                </h3>
                <p className="opacity-75 leading-relaxed text-[0.98rem]">{it.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
